export const GENERIC_CUSTOMER_OPERATION_ERROR = 'Your information has not been changed. Please try again.'

type SafeMessageMap = Readonly<Record<string, Readonly<Record<string, string>>>>

// Only exact, reviewed business outcomes are eligible for customer display.
// Everything else, including database, PostgREST and unexpected RPC detail,
// is reduced to the generic message below.
const SAFE_CUSTOMER_OPERATION_MESSAGES: SafeMessageMap = {
  assign_r1_confirmation: {
    'Confirmation request is not awaiting initial assignment': 'This confirmation is no longer awaiting assignment. Refresh to see its current status.',
    'Select an active organisation member': 'Select an active organisation member for this confirmation.',
    'Select an active member with the required specialist authority': 'Select an active organisation member who can complete this confirmation.',
  },
  reassign_r1_confirmation: {
    'Confirmation request is not open for reassignment': 'This confirmation is no longer open for reassignment. Refresh to see its current status.',
    'Confirmation assignment has changed': 'This confirmation was reassigned elsewhere. Refresh before trying again.',
    'Reason for reassignment is required': 'Add a short reason for the reassignment.',
    'Select a different reviewer': 'Select a different person for this confirmation.',
    'Select an active organisation member': 'Select an active organisation member for this confirmation.',
    'Select an active member with the required specialist authority': 'Select an active organisation member who can complete this confirmation.',
  },
  recall_r1_confirmation: {
    'Confirmation request is not open for recall': 'This confirmation is no longer open and cannot be recalled.',
    'Confirmation assignment has changed': 'This confirmation assignment changed. Refresh before trying again.',
    'Confirmation request is already unassigned': 'This confirmation is already awaiting a specialist assignment.',
    'Reason for recall is required': 'Add a short reason for recalling the assignment.',
  },
  cancel_r1_confirmation: {
    'The deterministic basis cannot be verified. Reassign the reviewer or start a reassessment.': 'The deterministic basis cannot be verified. Reassign the reviewer or start a reassessment.',
    'This confirmation is still required by the current Assessment and cannot be withdrawn. Reassign the reviewer instead.': 'This confirmation is still required by the current Assessment and cannot be withdrawn. Reassign the reviewer instead.',
    'Explain why this confirmation is being withdrawn': 'Add a short explanation when you choose Other.',
    'Confirmation request is not open for withdrawal': 'This confirmation is no longer open for withdrawal. Refresh to see its current status.',
  },
  request_r1_confirmation_information: {
    'Confirmation request is not open for information requests': 'This confirmation is no longer open for information requests. Refresh to see its current status.',
    'Confirmation requirement is not open for information requests': 'This confirmation is no longer open for information requests. Refresh to see its current status.',
    'Information request text is required': 'Enter the information you need before sending the request.',
  },
  answer_r1_assessment_information_request: {
    'Information request is no longer open': 'This information request is already closed. Refresh to see its current status.',
    'Your response is required': 'Enter your response before sending it.',
  },
  reassign_r1_assessment_information_request: {
    'Information request is no longer open': 'This information request is already closed and cannot be reassigned.',
    'Information request assignment has changed': 'This information request was reassigned elsewhere. Refresh before trying again.',
    'Reason for reassignment is required': 'Add a short reason for the reassignment.',
    'Select a different recipient': 'Select a different recipient for this information request.',
  },
  recall_r1_assessment_information_request: {
    'Information request is no longer open': 'This information request is already closed and cannot be recalled.',
    'Information request assignment has changed': 'This information request assignment changed. Refresh before trying again.',
    'Information request is already unassigned': 'This information request is already unassigned.',
    'Reason for recall is required': 'Add a short reason for recalling the assignment.',
  },
  cancel_r1_assessment_information_request: {
    'Information request is no longer open': 'This information request is already closed and cannot be withdrawn.',
    'Explain why this request is being withdrawn': 'Add a short explanation when you choose Other.',
  },
  respond_r1_confirmation: {
    'Confirmation requirement is not open': 'This confirmation is no longer open. Refresh to see its current status.',
    'Open information requests must be answered before a terminal specialist response': 'This confirmation is waiting for requested information. Resolve the open information requests before completing your confirmation.',
  },
  start_r1_confirmation_reassessment: {
    'Reason for reassessment is required': 'Enter the reason for reassessment before continuing.',
  },
  assign_r1_approval_requirement: {
    'Select an active organisation member who is compatible with this approval requirement': 'Select an active organisation member who can be assigned to this approval.',
  },
  reassign_r1_approval_requirement: {
    'Approval requirement is not open for reassignment': 'This approval is no longer open for reassignment. Refresh to see its current status.',
    'Approval assignment has changed': 'This approval assignment changed. Refresh before trying again.',
    'Reason for reassignment is required': 'Add a short reason for the reassignment.',
    'Select a different approver': 'Select a different person for this approval.',
    'Select an active organisation member who is compatible with this approval requirement': 'Select an active organisation member who is compatible with this approval.',
  },
  recall_r1_approval_requirement: {
    'Approval requirement is not open for recall': 'This approval is no longer open and cannot be recalled.',
    'Approval assignment has changed': 'This approval assignment changed. Refresh before trying again.',
    'Approval requirement is already unassigned': 'This approval is already awaiting an approver assignment.',
    'Reason for recall is required': 'Add a short reason for recalling the assignment.',
  },
  record_phase3_approval_decision: {
    'Decision rationale is required': 'Enter a decision rationale before recording the approval.',
    'Approval validity end must be after validity start': 'Set the approval end date after the start date.',
    'Decision must be recorded by the assigned approver': 'Only the person currently assigned to this approval can record the decision.',
    'Approval Decision requires an active Organisation membership': 'Your organisation membership must be active before you can record this approval decision.',
  },
}

export class CustomerSafeRpcError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CustomerSafeRpcError'
  }
}

export function toCustomerSafeRpcError(functionName: string, error: { message: string }): CustomerSafeRpcError {
  const customerMessage = SAFE_CUSTOMER_OPERATION_MESSAGES[functionName]?.[error.message]
    ?? GENERIC_CUSTOMER_OPERATION_ERROR
  return new CustomerSafeRpcError(customerMessage)
}

export function customerSafeOperationMessage(error: unknown): string {
  return error instanceof CustomerSafeRpcError ? error.message : GENERIC_CUSTOMER_OPERATION_ERROR
}
